
const TechnologyViewModel = require("../ViewModels/TechnologyViewModel");
const {TechnologyTypes, AgeOrder} = require("../Constants/Constants")
const { GetCorruption } = require("../Utilities/Utilities")
const { AvailablePools } = require("../Moves/Costs")
const { PoolKinds } = require("../Effects/Effects")
const LeaderNames = require("../Constants/Names/LeaderNames");
const WonderNames = require("../Constants/Names/WonderNames");


// Columns of the building grid, in BGO's order: production, then urban, then military, each alphabetical
const TechnologyOrder = [
    TechnologyTypes.Production.Farm,
    TechnologyTypes.Production.Mine,
    TechnologyTypes.Urban.Arena,
    TechnologyTypes.Urban.Lab,
    TechnologyTypes.Urban.Library,
    TechnologyTypes.Urban.Temple,
    TechnologyTypes.Urban.Theater,
    TechnologyTypes.Military.AirForces,
    TechnologyTypes.Military.Artillery,
    TechnologyTypes.Military.Cavalry,
    TechnologyTypes.Military.Infantry,
]

const SpecialTypes = Object.values(TechnologyTypes.Special);



// The card of the player's current tactic: their own (just played), the common one they are on (by card, else by name),
// or for a tactic set up by name only (e.g. a test game) the card itself
const TacticCard = (playerStateData) => playerStateData.exclusiveTactic
    ?? playerStateData.gameState?.publicTactics.find(card => card.id === playerStateData.currentTacticId)
    ?? playerStateData.gameState?.publicTactics.find(card => card.name === playerStateData.currentTactic)
    ?? require('../Utilities/Converters/TacticConverter')(playerStateData.currentTactic) ?? null;

class PlayerViewModel{
    constructor(playerStateData)
    {
        this.name = playerStateData.name;
        this.playerNumber = playerStateData.playerNumber;
        this.culture = playerStateData.culture;
        this.cultureProduction = playerStateData.GetCultureProduction();
        this.science = playerStateData.science;
        this.scienceProduction = playerStateData.GetScienceProduction();
        this.resourceProduction = playerStateData.GetResourceProduction();
        this.currentResources = playerStateData.GetResources();
        // (less what the workers eat)
        this.foodProduction = playerStateData.GetNetFoodProduction();
        this.currentFood = playerStateData.GetFood();
        this.happyFaces = playerStateData.GetHappyFaces();
        this.militaryStrength = playerStateData.GetMilitaryStrength();
        this.colonizationBonus = playerStateData.GetColonizationBonus();
        // How many buildings of each urban kind the player may have (the government's, raised by the Acropolis)
        this.urbanBuildingLimit = playerStateData.GetUrbanBuildingLimit();

        this.government = playerStateData.government;
        // Its card, shown on hover (its urban building limit, civil and military actions)
        this.governmentCard = require('../Utilities/Converters/TechnologyConverter')(playerStateData.government,
            playerStateData.TechnologyVersion(playerStateData.government));
        this.civilActionCount = playerStateData.GetCivilActionCount();
        // Civil actions that are Hammurabi's military action used as a civil action (shown in parentheses)
        this.militaryAsCivilActions = playerStateData.GetMilitaryAsCivilActions();
        this.civilHandLimit = playerStateData.GetCivilHandLimit();
        this.militaryHandLimit = playerStateData.GetMilitaryHandLimit();
        // (the red tokens on the board: this turn's too, e.g. Patriotism's, though they are not part of the total)
        this.militaryActionCount = playerStateData.GetMilitaryActionCount() + playerStateData.GetTemporaryMilitaryActions();
        this.civilActionsRemaining = playerStateData.GetCivilActionsRemaining();
        // Civil actions for some actions only, shown as tokens of their own (Gutenberg's): [{ source, purpose, used }]
        this.restrictedCivilActions = playerStateData.GetRestrictedCivilActions();
        // Saladin's choice, kept until the next one ('strength' / 'civil' / null)
        this.saladinChoice = playerStateData.CardState(LeaderNames.Saladin).choice ?? null;
        this.militaryActionsRemaining = playerStateData.GetMilitaryActionsRemaining();
        this.workerPool = playerStateData.workerPool;
        this.breakdowns = playerStateData.GetBreakdowns();
        this.corruption = GetCorruption(playerStateData.blueBank);
        // Extra resources left this turn for military units only (e.g. Patriotism, Homer): [{ source, amount }]
        this.militaryPools = AvailablePools(playerStateData, PoolKinds.MilitaryResources);
        // Extra science left this turn for military unit technologies only (e.g. Churchill)
        this.militarySciencePools = AvailablePools(playerStateData, PoolKinds.MilitaryScience);
        // Resources for wonders and urban buildings only (Cleopatra)
        this.wonderUrbanPools = AvailablePools(playerStateData, PoolKinds.WonderUrbanResources);
        // Yellow tokens above the government card (Hippocrates)
        this.hippocratesTokens = playerStateData.CardState(LeaderNames.Hippocrates).tokens ?? 0;
        // Blue tokens kept on the completed Louvre Museum
        this.louvreTokens = playerStateData.CardState(WonderNames.LouvreMuseum).tokens ?? 0;
        this.discontentWorkers = playerStateData.GetDiscontentWorkers();
        this.uprising = playerStateData.HasUprising();
        this.leader = playerStateData.leader;
        // The tactic card in use: the player's own if not yet common, otherwise the common one of that name
        // (played this turn: the player's alone until their next turn)
        this.tacticIsPrivate = !!playerStateData.exclusiveTactic;
        // Tactics replaced while Sun Tzu was in play, still in the player's area (no one can copy them)
        this.keptTactics = playerStateData.keptTactics ?? [];
        // (with what Sun Tzu added to that card, if anything)
        this.currentTactic = playerStateData.currentTactic === null ? null : require('./TacticBonus').WithTacticBonus(TacticCard(playerStateData), playerStateData.gameState);
        this.wonders = playerStateData.wonders;
        // Territories colonized
        this.colonies = playerStateData.colonies ?? [];
        // The armies formed for the current tactic: { full, obsolete, doubled, value }
        this.tacticArmies = playerStateData.GetTacticArmies();
        // The unfinished wonder: every stage's resource cost, the stages built so far (covered by blue tokens),
        // and the costs of the stages still to build
        this.wonderInProgress = playerStateData.wonderInProgress && {
            card: playerStateData.wonderInProgress.card,
            stageCosts: playerStateData.wonderInProgress.card.stageCosts,
            stagesBuilt: playerStateData.wonderInProgress.stagesBuilt,
            // Who built each stage, by name (others may build an International Red Cross's), when not all its owner
            builtBy: (playerStateData.wonderInProgress.stagesBy ?? []).map(by => playerStateData.gameState?.playerStates[by]?.name),
            remainingCosts: playerStateData.wonderInProgress.card.stageCosts.slice(playerStateData.wonderInProgress.stagesBuilt),
        };

        this.civilHand = playerStateData.civilHand;
        // The events this player has prepared, still face down in the current and future events: [{ age, name?, card? }],
        // with the card itself when the one looking knows what it is (their own), to show as in a hand
        const prepared = playerStateData.gameState?.GetEventsPrepared() ?? { current: [], future: [] };
        let preparedId = -900000;
        const asCard = (event) => {
            if (!event.name || !event.type) return null;
            try { return require('../Utilities/Converters/CardConverter')({ type: event.type, name: event.name, age: event.age, id: preparedId-- }); }
            catch (e) { return null; }
        };
        const mine = (cards) => cards.filter(card => card.player === playerStateData.playerNumber).map(card => ({ ...card, card: asCard(card) }));
        this.eventsPlayed = { current: mine(prepared.current), future: mine(prepared.future) };
        this.militaryHand = playerStateData.militaryHand;

        this.blueBank = playerStateData.blueBank;
        this.yellowBank =  playerStateData.yellowBank;

        // The building grid: one column per type in play and one row per age from Age A up to the most
        // advanced card, newest age first (so the grid grows upwards). Empty cells are undefined.
        const gridTechs = playerStateData.technologies.filter(tech => TechnologyOrder.includes(tech.type));
        this.techTypesUsed = TechnologyOrder.filter(type => gridTechs.some(tech => tech.type === type));
        const highestAge = Math.max(0, ...gridTechs.map(tech => AgeOrder.indexOf(tech.age)));
        this.technologyGrid = AgeOrder.slice(0, highestAge + 1).reverse().map(age => ({
            age,
            Techs: this.techTypesUsed.map(type => {
                const tech = gridTechs.find(t => t.age === age && t.type === type);
                return tech ? new TechnologyViewModel(tech, playerStateData) : undefined;
            }),
        }));

        // Special technologies (civil, construction, colonization, military) are shown on their own
        this.specialTechnologies = playerStateData.technologies.filter(tech => SpecialTypes.includes(tech.type));
    }
}

module.exports = PlayerViewModel;